import { InternalAddress } from "./address";
import { checkoutAddressFormFieldKey } from "@swo/shared";
import { notNullPredicate } from "../../../../../../../utils/fp/missing-ts-parts";
import { getAvailableCountryStates } from "../country-states/get-available-country-states";
import { translatedCtpField } from "./translatedCtpField";
import { InternationalPhoneNumber } from "./international-phone-number/international-phone-number";
import { CountryCodeToDialCode } from "./international-phone-number/country-iso-code-to-dial-code";
import { Translated } from "../../../../../../../domain/translations/translations";
import { AddressFormMode } from "./address-form-mode";
import { FormField } from "./types";
import { localizedCountryName } from "./localizedCountryName";
import {
  SwarovskiOptikLocaleFrontend,
  SwarovskiOptikShopLocaleFrontend,
  type CheckoutTranslation,
} from "@swo/storefront";
import { arrayGroupByTransform } from "../../../../../../../utils/fp/group-by";

export function getFormFields(
  setFormState: React.Dispatch<React.SetStateAction<InternalAddress>>,
  translations: Translated<CheckoutTranslation>,
  locale: SwarovskiOptikShopLocaleFrontend,
  mode: AddressFormMode,
  onChangeCountryRequested: (countryIsoCode: string) => void,
  locales: Array<SwarovskiOptikShopLocaleFrontend> | null,
): Array<FormField> {
  const tField = translatedCtpField(translations);
  const availableCountryStates = getAvailableCountryStates(locale.country.isoCode);

  const dialCodesList = localisedDialCodesList(locale.language.isoCode);
  // const billingOrShipping: "billing" | "shipping" = mode === "shipping" ? "shipping" : "billing";

  const emptyPhoneValue =
    dialCodesList.find(d => d.value.toUpperCase() === locale.country.isoCode.toUpperCase()) || dialCodesList[0]!;

  const canEditUserEmail = mode === "shipping";
  const hasPersonalDataFields = mode === "shipping";

  function getCountriesField(): FormField {
    if (locales === null) {
      return {
        key: checkoutAddressFormFieldKey.country,
        fieldName: "country" as const,
        field: {
          tag: "input" as const,
          value: (state: InternalAddress) => localizedCountryName(state.country, locale.language.isoCode),
          onChange: (value: string) => {},
        },
        placeholder: tField("country").value,
        disabled: true,
        required: "yes-but-without-native-validation" as const,
        autoComplete: "country",
      };
    } else {
      const localesGroupedByCountryIsoCode = arrayGroupByTransform(
        locales,
        _ => _.country.isoCode.toUpperCase(),
        _ => ({ localeCode: _.code, langaugeIsoCode: _.language.isoCode }),
      );

      const values = Object.entries(localesGroupedByCountryIsoCode)
        .map(([countryIsoCode, languages]) => {
          const l = languages[0]!;

          return { countryIsoCode };
        })
        .map(({ countryIsoCode }) => {
          const localCountryName = localizedCountryName(countryIsoCode, locale.language.isoCode);

          return {
            label: localCountryName,
            value: countryIsoCode,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      const emptyValue = {
        value: locale.country.isoCode,
        label: locale.country.label,
      };

      return {
        key: checkoutAddressFormFieldKey.country,
        fieldName: "country" as const,
        field: {
          tag: "dropdown" as const,
          value: (state: InternalAddress) => state.country,
          values: values,
          onChange: (countryIsoCode: string) => {
            onChangeCountryRequested(countryIsoCode);
          },
          emptyValue: emptyValue,
        },
        placeholder: tField("country").value,
        required: "yes-but-without-native-validation" as const,
        autoComplete: "country",
      };
    }
  }

  return [
    ...(hasPersonalDataFields
      ? [
          {
            key: checkoutAddressFormFieldKey.firstName,
            fieldName: "firstName" as const,
            field: {
              tag: "input" as const,
              value: (state: InternalAddress) => state.firstName,
              onChange: (value: string) => setFormState(s => ({ ...s, firstName: value })),
            },
            placeholder: tField("firstName").value,
            required: "yes-but-without-native-validation" as const,
            autoComplete: "given-name",
          },
          {
            key: checkoutAddressFormFieldKey.lastName,
            fieldName: "lastName" as const,
            field: {
              tag: "input" as const,
              value: (state: InternalAddress) => state.lastName,
              onChange: (value: string) => setFormState(s => ({ ...s, lastName: value })),
            },
            placeholder: tField("lastName").value,
            required: "yes-but-without-native-validation" as const,
            autoComplete: "family-name",
          },
        ]
      : []),
    {
      key: checkoutAddressFormFieldKey.addressOne,
      fieldName: "streetName" as const,
      field: {
        tag: "input" as const,
        value: (state: InternalAddress) => state.addressLineOne,
        onChange: (value: string) => setFormState(s => ({ ...s, addressLineOne: value })),
      },
      placeholder: tField("streetName").value,
      required: "yes-but-without-native-validation" as const,
      autoComplete: `${mode} address-line1`,
    },
    {
      key: checkoutAddressFormFieldKey.addressTwo,
      fieldName: "additionalAddressInfo" as const,
      field: {
        tag: "input" as const,
        value: (state: InternalAddress) => state.addressLineTwo,
        onChange: (value: string) => setFormState(s => ({ ...s, addressLineTwo: value })),
      },
      placeholder: tField("additionalAddressInfo").value,
      required: false,
      autoComplete: `${mode} address-line2`,
    },
    {
      key: checkoutAddressFormFieldKey.postalCode,
      fieldName: "postalCode" as const,
      field: {
        tag: "input" as const,
        value: (state: InternalAddress) => state.postalCode,
        onChange: (value: string) => setFormState(s => ({ ...s, postalCode: value })),
      },
      placeholder: tField("postalCode").value,
      required: "yes-but-without-native-validation" as const,
      autoComplete: `${mode} postal-code`,
    },
    {
      key: checkoutAddressFormFieldKey.city,
      fieldName: "city" as const,
      field: {
        tag: "input" as const,
        value: (state: InternalAddress) => state.city,
        onChange: (value: string) => setFormState(s => ({ ...s, city: value })),
      },
      placeholder: tField("city").value,
      required: "yes-but-without-native-validation" as const,
      autoComplete: `${mode} address-level2`,
    },
    availableCountryStates
      ? {
          key: checkoutAddressFormFieldKey.state,
          fieldName: "state" as const,
          field: {
            tag: "dropdown" as const,
            value: (state: InternalAddress) => state.state,
            values: availableCountryStates,
            onChange: (value: string) => setFormState(s => ({ ...s, state: value })),
            emptyValue: {
              value: "",
              label: translations("addressForm.state").value,
            },
          },
          placeholder: tField("state").value,
          required: "yes-but-without-native-validation" as const,
          autoComplete: `${mode} address-level1`,
        }
      : null,
    getCountriesField(),
    {
      key: checkoutAddressFormFieldKey.phone,
      fieldName: "phone" as const,
      field: {
        tag: "phone" as const,
        value: (state: InternalAddress) => state.phoneNumber,
        onChange: (value: InternationalPhoneNumber) => setFormState(s => ({ ...s, phoneNumber: value })),
        dialCodes: dialCodesList,
        emptyValue: emptyPhoneValue,
      },
      required: "yes-but-without-native-validation" as const,
      placeholder: tField("phone").value,
      type: "tel",
      autoComplete: `${mode} tel-local`,
    },
    canEditUserEmail
      ? {
          key: checkoutAddressFormFieldKey.email,
          fieldName: "email" as const,
          field: {
            tag: "input" as const,
            value: (state: InternalAddress) => state.email,
            onChange: (value: string) => setFormState(s => ({ ...s, email: value })),
          },
          placeholder: tField("email").value,
          type: "email",
          required: "yes-but-without-native-validation" as const,
          autoComplete: `${mode} email`,
        }
      : null,
  ].filter(notNullPredicate);
}

const COUNTRIES_WITH_DIAL_CODE_PREFIXES = Object.entries(CountryCodeToDialCode).map(
  ([countryIsoCode, dialCodePrefix]) => {
    return { countryIsoCode, dialCodePrefix };
  },
);

const localisedDialCodesList = (languageIsoCode: string) =>
  COUNTRIES_WITH_DIAL_CODE_PREFIXES.map(({ countryIsoCode, dialCodePrefix }) => {
    const countryName = localizedCountryName(countryIsoCode, languageIsoCode);

    return {
      label: `${countryName} (${dialCodePrefix})`,
      shortLabel: dialCodePrefix,
      value: countryIsoCode,
    };
  }).sort((a, b) => a.label.localeCompare(b.label));
